/**
 * The Settings curator queue.
 *
 * Two views, not one merged list. The accepted API lists exactly one
 * publication_state per request, so presenting a single blended queue would be a
 * claim the response does not support; each tab is one honest read of one state.
 *
 * Pending review offers Approve and Reject. Rejected offers Approve and Reopen.
 * Published signals are absent by contract - they are unpublished from the
 * signal's own modal, where the published version can be read authoritatively.
 */

import { useState } from 'react';
import { useCuratorDecision, useCuratorQueue } from '../hooks';
import type { DecisionIntent } from '../decision-intent';
import {
  LISTABLE_QUEUE_STATES,
  PUBLICATION_STATE_LABELS,
  type CuratorQueueRow as QueueRow,
  type DecisionKind,
  type ListableQueueState,
} from '../types';
import { CuratorQueueRow } from './CuratorQueueRow';

export function CuratorQueue(): JSX.Element {
  const [state, setState] = useState<ListableQueueState>('pending_review');
  const queue = useCuratorQueue(state);
  const [activeSignalId, setActiveSignalId] = useState<string | null>(null);
  const decision = useCuratorDecision({
    onSuccess: () => setActiveSignalId(null),
  });

  const [refreshing, setRefreshing] = useState(false);

  const refreshAuthoritativeState = async () => {
    // The single recovery path after a conflict, and it AWAITS the re-read. The
    // block exists because the expected state and version in hand are stale;
    // releasing it before fresh truth arrives would let the curator resubmit
    // against the same stale values and simply collect the same 409. A failed
    // re-read leaves the conflict standing, which is the honest outcome: nothing
    // authoritative was learned.
    if (refreshing) return;
    setRefreshing(true);
    try {
      const result = await queue.refetch();
      if (result.isError || result.data === undefined) return;
      decision.clearConflict();
    } finally {
      setRefreshing(false);
    }
  };

  const decide = (row: QueueRow, kind: DecisionKind, reason: string | null) => {
    const intent: DecisionIntent = {
      signalId: row.signalId,
      kind,
      reason,
      metadata: {},
      // Taken from the row exactly as read. Never inferred, never defaulted:
      // an expected version the client made up is the one thing that turns a
      // safe conflict into a wrong decision.
      expectedPublicationState: row.publicationState,
      expectedPublicationVersion: row.publicationVersion,
    };
    // Ownership of the in-flight state is recorded ONLY once the shared
    // controller has accepted the attempt. Assigning it first meant a refused
    // second row - one arriving while the first request was still in flight -
    // still took the active slot, and the first request's busy, error and
    // conflict state then rendered against a row that had never been submitted.
    if (decision.submit(intent)) setActiveSignalId(row.signalId);
  };

  // No landmark of its own. Settings already wraps this component in the single
  // labelled curation section; nesting a second identically-named landmark
  // inside it announced the same region twice to a screen reader.
  return (
    <div>
      <div className="bdse-nav-section" style={{ paddingLeft: 0 }}>
        Signal curation
      </div>
      <p className="bdse-page-subtitle mb-3">
        Review newly gathered signals before they reach users. Approve publishes to the feed; Reject
        keeps a signal out of the feed; Rejected lets you revisit prior decisions.
      </p>

      <div className="curator-toolbar">
        <div className="curator-tabs" role="tablist" aria-label="Queue state">
          {LISTABLE_QUEUE_STATES.map((option) => (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={state === option}
              className={`sx-btn ${state === option ? 'sx-btn-p2' : 'sx-btn-p3'}`}
              onClick={() => setState(option)}
            >
              {PUBLICATION_STATE_LABELS[option]}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="sx-btn sx-btn-p3 curator-refresh"
          disabled={queue.isFetching}
          onClick={() => void queue.refetch()}
        >
          {queue.isFetching ? 'Refreshing…' : 'Refresh list'}
        </button>
      </div>

      <p className="curator-view-help">
        {state === 'pending_review'
          ? 'Pending review contains signals no ordinary user can see yet.'
          : 'Rejected is the decision history you can reopen or approve if new evidence changes the decision.'}{' '}
        Refresh list only re-reads this queue; it does not run a source sweep.
      </p>

      {queue.status === 'pending' ? (
        <p className="sx-helper" role="status">
          Loading the curation queue…
        </p>
      ) : null}

      {queue.error ? (
        <p className="sx-popover-help sx-popover-help--error" role="alert">
          {queue.error.message}
        </p>
      ) : null}

      {decision.result ? (
        <p className="sx-helper" role="status">
          {decision.result.idempotent
            ? 'That decision had already been recorded — nothing was applied twice.'
            : 'Decision recorded.'}
        </p>
      ) : null}

      {/*
        The empty state is a claim, so it renders only after a SUCCESSFUL empty
        read. On a failed read the error stands alone and any rows already loaded
        stay visible: "nothing to review" is not something a failed request can
        establish.
      */}
      {queue.status === 'success' && queue.rows.length === 0 ? (
        <p className="sx-helper">No {PUBLICATION_STATE_LABELS[state].toLowerCase()} signals.</p>
      ) : null}

      <ul style={{ listStyle: 'none', padding: 0 }}>
        {queue.rows.map((row) => (
          <CuratorQueueRow
            key={row.signalId}
            row={row}
            /*
              EVERY row is disabled while the shared mutation is in flight, not
              only the one being decided. One mutation and one decision-key
              controller serve the whole list, so a second row starting mid
              request would take over the controller and leave the in-flight
              attempt's key unreachable for its own retry. Disabling only the
              active row left exactly that gap open.
            */
            disabled={decision.isPending || refreshing}
            busy={decision.isPending && activeSignalId === row.signalId}
            /* The acted-on row alone owns the outcome message. */
            error={activeSignalId === row.signalId ? decision.error : null}
            conflicted={activeSignalId === row.signalId && decision.isConflicted}
            blockReason={activeSignalId === row.signalId ? decision.blockReason : null}
            refreshing={refreshing}
            onDecide={(kind, reason) => decide(row, kind, reason)}
            onRefresh={() => void refreshAuthoritativeState()}
          />
        ))}
      </ul>

      {queue.hasNextPage ? (
        <button
          type="button"
          className="sx-btn sx-btn-p3"
          disabled={queue.isFetchingNextPage}
          onClick={queue.fetchNextPage}
        >
          {queue.isFetchingNextPage ? 'Loading…' : 'Show more'}
        </button>
      ) : null}
    </div>
  );
}
